import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery } from '@tanstack/react-query'
import {
  Card,
  Form,
  Input,
  Switch,
  Select,
  Button,
  message,
  Row,
  Col,
  Typography,
  Space,
  Alert,
} from 'antd'
import { SaveOutlined, CloseOutlined, InfoCircleOutlined, WarningOutlined } from '@ant-design/icons'
import { createFeatureFlag, getFeatureFlagByKey } from '@/api/features'
import type { FeatureFlagCreateInput } from '@/types/feature'
import { useState } from 'react'

const { Title, Text } = Typography

export const FeatureCreate = () => {
  const navigate = useNavigate()
  const [form] = Form.useForm()
  const [featureKey, setFeatureKey] = useState('')
  const [checkKey, setCheckKey] = useState(false)

  // Check if feature key already exists (debounced)
  const { data: existingFlag, isLoading: checkingKey } = useQuery({
    queryKey: ['features', 'check-key', featureKey],
    queryFn: () => getFeatureFlagByKey(featureKey),
    enabled: checkKey && featureKey.length > 0 && /^[a-z0-9-]+$/.test(featureKey),
    retry: false,
  })

  const keyExists = !!existingFlag?.data

  // Create feature flag mutation
  const createMutation = useMutation({
    mutationFn: createFeatureFlag,
    onSuccess: (response) => {
      message.success('Feature flag created successfully')
      navigate(`/features/${response.data.id}`)
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.message || 'Failed to create feature flag')
    },
  })

  const handleSubmit = (values: FeatureFlagCreateInput) => {
    if (keyExists) {
      message.error('This feature key already exists. Please choose a different key.')
      return
    }
    createMutation.mutate(values)
  }

  const handleCancel = () => {
    navigate('/features')
  }

  const handleFeatureKeyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setFeatureKey(value)
    // Only check after user has typed a reasonable key
    setCheckKey(value.length >= 3)
  }

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={2} style={{ margin: 0 }}>
            Create Feature Flag
          </Title>
        </Col>
      </Row>

      <Card>
        <Alert
          message="Feature Key and Scope are Immutable"
          description="Once created, the feature key and scope cannot be changed. Choose carefully!"
          type="info"
          icon={<InfoCircleOutlined />}
          showIcon
          style={{ marginBottom: 24 }}
        />

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            default_value: false,
            scope: 'user',
            flag_type: 'application',
          }}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="feature_key"
                label="Feature Key"
                validateStatus={
                  checkingKey
                    ? 'validating'
                    : keyExists
                      ? 'error'
                      : featureKey && !keyExists
                        ? 'success'
                        : undefined
                }
                hasFeedback={checkKey}
                help={
                  keyExists ? (
                    <span style={{ color: '#ff4d4f' }}>
                      <WarningOutlined /> This feature key already exists
                    </span>
                  ) : checkingKey ? (
                    'Checking availability...'
                  ) : featureKey && !keyExists && checkKey ? (
                    <span style={{ color: '#52c41a' }}>Feature key is available</span>
                  ) : undefined
                }
                rules={[
                  { required: true, message: 'Feature key is required' },
                  {
                    pattern: /^[a-z0-9-]+$/,
                    message:
                      'Feature key must contain only lowercase letters, numbers, and hyphens',
                  },
                  {
                    validator: (_, value) => {
                      if (value && (value.startsWith('-') || value.endsWith('-'))) {
                        return Promise.reject('Feature key cannot start or end with a hyphen')
                      }
                      if (keyExists) {
                        return Promise.reject('This feature key already exists')
                      }
                      return Promise.resolve()
                    },
                  },
                ]}
                extra="Unique identifier (lowercase, numbers, hyphens only)"
              >
                <Input placeholder="my-feature-flag" onChange={handleFeatureKeyChange} />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                name="label"
                label="Display Label"
                rules={[{ required: true, message: 'Label is required' }]}
                extra="Human-readable name for this feature"
              >
                <Input placeholder="My Feature Flag" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="scope"
                label="Scope"
                rules={[{ required: true, message: 'Scope is required' }]}
                extra="Who can this feature be enabled for?"
              >
                <Select>
                  <Select.Option value="user">
                    <Space>
                      <Text>User</Text>
                      <Text type="secondary">- Individual users</Text>
                    </Space>
                  </Select.Option>
                  <Select.Option value="facility">
                    <Space>
                      <Text>Facility</Text>
                      <Text type="secondary">- Hospitals/facilities</Text>
                    </Space>
                  </Select.Option>
                </Select>
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                name="flag_type"
                label="Flag Type"
                rules={[{ required: true, message: 'Flag type is required' }]}
                extra="Classification of this feature flag"
              >
                <Select>
                  <Select.Option value="application">
                    <Space>
                      <Text>Application</Text>
                      <Text type="secondary">- App feature</Text>
                    </Space>
                  </Select.Option>
                  <Select.Option value="config">
                    <Space>
                      <Text>Config</Text>
                      <Text type="secondary">- Configuration</Text>
                    </Space>
                  </Select.Option>
                </Select>
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                name="default_value"
                label="Default Value"
                valuePropName="checked"
                extra="Default state when not explicitly set"
              >
                <Switch checkedChildren="Enabled" unCheckedChildren="Disabled" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item style={{ marginTop: 24 }}>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                icon={<SaveOutlined />}
                loading={createMutation.isPending || checkingKey}
                disabled={keyExists}
              >
                Create Feature Flag
              </Button>
              <Button
                icon={<CloseOutlined />}
                onClick={handleCancel}
                disabled={createMutation.isPending}
              >
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>
    </div>
  )
}
